
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, MessagesSquare, Users } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import { communityFeatures, platformModules } from "@/assets/data/community";
import { PLATFORM_ROUTES } from "@/lib/platform";


const CommunitySection = () => {
  const { t, language } = useLanguage();

  return (
    <section className="py-16 md:py-24 bg-gray-50" id="community">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <Badge className="bg-nexus-yellow/20 text-nexus-gray hover:bg-nexus-yellow/30 mb-4">{t("community.communityLabel")}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray mb-6">
              {t("community.title")}
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              {t("community.description")}
            </p>

            <div className="space-y-4 mb-8">
              {communityFeatures.map((feature, index) => (
                <div key={index} className="flex gap-3 transition-transform duration-300 hover:translate-x-1 rtl:hover:-translate-x-1">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full ${feature.bgColor} flex items-center justify-center`}>
                    <feature.icon className={`h-5 w-5 ${feature.color}`} />
                  </div>
                  <div>
                    <h3 className={`font-medium text-nexus-gray`}>
                      {t(feature.title)}
                    </h3>
                    <p className="text-sm text-gray-500">{t(feature.description)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="group bg-nexus-brand hover:bg-nexus-brand/90 dark:text-white" >
                <a href={PLATFORM_ROUTES.community} target="_blank" rel="noopener noreferrer">
                  <span>{t("community.visitCommunityButton")}</span>
                  <ArrowRight className={`ms-2 h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 ${language === "ar" ? "rotate-180" : ""}`}/>
                </a>
              </Button>
              <Button asChild variant="outline" className="border-nexus-gray text-nexus-gray hover:bg-nexus-gray/5 dark:text-nexus-light dark:hover:text-nexus-gray">
                <a href={PLATFORM_ROUTES.mentorship} target="_blank" rel="noopener noreferrer">
                  {t("community.becomeMentorButton")}
                </a>
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white rounded-lg shadow-lg p-6 relative z-10">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-nexus-gray">{t("community.highlights.title")}</h3>
                <Badge className="bg-nexus-teal text-white">{t("community.highlights.label")}</Badge>
              </div>

              <div className="space-y-4 mb-6">
                <Card className="border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-nexus-teal/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-nexus-teal" />
                      </div>
                      <div>
                        <h4 className="font-medium text-nexus-gray dark:text-nexus-light">{t("community.highlights.studyGroups.title")}</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{t("community.highlights.studyGroups.description")}</p>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{t("community.highlights.studyGroups.action")}</span>
                      <span className="text-nexus-teal font-medium">{t("community.highlights.studyGroups.status")}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-nexus-red/10 flex items-center justify-center">
                        <MessagesSquare className="h-5 w-5 text-nexus-red" />
                      </div>
                      <div>
                        <h4 className="font-medium text-nexus-gray dark:text-nexus-light">{t("community.highlights.discussions.title")}</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{t("community.highlights.discussions.description")}</p>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">{t("community.highlights.discussions.action")}</span>
                      <span className="text-nexus-teal font-medium">{t("community.highlights.discussions.status")}</span>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-gradient-to-r from-nexus-teal/20 to-nexus-yellow/20 p-4 rounded-lg">
                  <h4 className="font-medium text-nexus-gray dark:text-nexus-light mb-3">{t("community.modules.title")}</h4>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {platformModules.map((module) => (
                      <div key={module.label} className="flex items-center gap-2 rounded-md bg-white/70 px-2.5 py-2 text-sm text-nexus-gray dark:bg-nexus-dark/60 dark:text-nexus-light">
                        <module.icon className="h-4 w-4 flex-shrink-0 text-nexus-teal" />
                        <span className="truncate">{t(module.label)}</span>
                      </div>
                    ))}
                  </div>
                  <a href={PLATFORM_ROUTES.community} target="_blank" rel="noopener noreferrer" className="text-sm text-nexus-teal font-medium hover:underline">
                    {t("community.modules.link")} {language === "ar" ? "←" : "→"}
                  </a>
                </div>
              </div>

              <Button asChild variant="outline" className="group w-full">
                <a href={PLATFORM_ROUTES.signup} target="_blank" rel="noopener noreferrer">
                  {t("community.highlights.ctaButton")}
                  <ArrowRight className={`ms-2 h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 ${language === "ar" ? "rotate-180" : ""}`} />
                </a>
              </Button>
            </div>

            {/* Decorative elements */}
            <div className="absolute w-full h-full top-4 right-4 rounded-lg border-2 border-dashed border-nexus-teal/30 -z-10"></div>
            <div className="absolute w-full h-full top-8 right-8 rounded-lg border-2 border-dashed border-nexus-yellow/30 -z-20"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunitySection;
